import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMsal } from '@azure/msal-react';
import { useNonce } from '../api/session.ts';
import { classifyLoginError, isLoginErrorKind, type LoginErrorKind } from '../auth/login-error.ts';
import { startLogin } from '../auth/msal.ts';
import { LoginPage } from '../features/login/login-page.tsx';

export interface LoginSearch {
  readonly error?: LoginErrorKind | undefined;
}

/**
 * Only known failure types survive; anything else in the URL is dropped, never echoed. The key is
 * always returned: TanStack Router merges raw search under the validated object, so omitting
 * `error` would let an unvalidated value through.
 */
export function validateLoginSearch(search: Record<string, unknown>): LoginSearch {
  const raw = search['error'];
  return { error: isLoginErrorKind(raw) ? raw : undefined };
}

export const Route = createFileRoute('/login')({
  validateSearch: validateLoginSearch,
  component: LoginRoute,
});

function LoginRoute(): React.JSX.Element {
  const { error } = Route.useSearch();
  const { entra } = Route.useRouteContext();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const nonce = useNonce();
  const onSignIn = async (): Promise<void> => {
    try {
      await startLogin(instance, entra, window.location.origin, () => nonce.mutateAsync());
    } catch (err) {
      void navigate({ to: '/login', search: { error: classifyLoginError(err) }, replace: true });
      throw err;
    }
  };
  return <LoginPage onSignIn={onSignIn} error={error} />;
}
